import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(dateString) {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getInitials(name) {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function truncate(str, length = 50) {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

/**
 * Normalize a user-entered URL so it opens externally in a new tab.
 *
 * Vision-deck / proposal-builder CTAs accept URLs like "myurl.com" or
 * "www.example.com" — without a protocol the browser treats them as
 * relative paths and redirects back to the app. This helper prepends
 * `https://` for those cases while preserving:
 *   - absolute URLs (http/https)
 *   - protocol-relative URLs (//cdn...)
 *   - mailto: / tel: / sms: links
 *   - in-app anchors (#section) and root-relative paths (/page)
 *
 * Also auto-prepends `mailto:` when the input looks like a bare email
 * address (e.g. user typed "hello@example.com" into the CTA URL field).
 *
 * Returns "#" for empty/whitespace input so the <a> tag stays inert.
 */
export function normalizeExternalUrl(url) {
  const raw = (url || '').trim();
  if (!raw) return '#';
  if (/^(https?:)?\/\//i.test(raw)) return raw;
  if (/^(mailto:|tel:|sms:|#|\/)/i.test(raw)) return raw;
  // Bare email detection — single @, no whitespace, valid TLD-ish ending.
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return `mailto:${raw}`;
  return `https://${raw}`;
}

export const PROPERTY_TYPES = [
  'Hotel',
  'Resort',
  'Restaurant',
  'Casino',
  'Cruise Line',
  'Private Club',
  'Spa & Wellness',
  'Event Venue',
  'Other'
];

export const REGIONS = [
  'North America',
  'Europe',
  'Asia Pacific',
  'Middle East',
  'Latin America',
  'Africa'
];

export const PIPELINE_STAGE_COLORS = {
  lead_captured: 'bg-slate-500',
  intake_submitted: 'bg-blue-500',
  discovery_call_completed: 'bg-cyan-500',
  design_deck_in_development: 'bg-purple-500',
  design_deck_delivered: 'bg-violet-500',
  client_reviewing: 'bg-amber-500',
  revision_phase: 'bg-orange-500',
  approved: 'bg-teal-500',
  production_vendor_alignment: 'bg-indigo-500',
  closed_won: 'bg-emerald-500',
  closed_lost: 'bg-red-500',
  draft: 'bg-slate-400',
};

export const STAGE_DISPLAY_NAMES = {
  prospecting: 'Prospecting',
  strategic_discovery: 'Strategic Discovery',
  vision_deck_presented: 'Vision Deck Presented',
  refinement: 'Refinement',
  production_alignment: 'Production Alignment',
  closed_won: 'Closed – Won',
  closed_lost: 'Closed – Lost',
  // Legacy stage mappings for backwards compatibility
  lead_captured: 'Prospecting',
  intake_submitted: 'Prospecting',
  discovery_call_completed: 'Strategic Discovery',
  executive_deck_presented: 'Vision Deck Presented',
  design_deck_in_development: 'Vision Deck Presented',
  design_deck_delivered: 'Vision Deck Presented',
  client_reviewing: 'Refinement',
  revision_phase: 'Refinement',
  approved: 'Production Alignment',
  production_vendor_alignment: 'Production Alignment',
  draft: 'Prospecting',
  qualified: 'Strategic Discovery',
  lead: 'Prospecting',
};
